/**
 * The auth-guard decision: given the current auth state and address bar,
 * where should the customer be — or nowhere, if they are already there.
 *
 * Pure, so it is testable without React Router, Supabase, or a mounted
 * component. This is the client-side layer of the THREE-layer activation
 * gate (ADR-0003, see the module doc on `AuthContext.tsx` for the other two).
 * Losing this layer is only a convenience regression — the BFF preHandler
 * and Postgres RLS are what actually keep an unactivated user out — but it
 * still must not be bypassable by navigating elsewhere, which is exactly
 * what `guard.test.ts` proves.
 */
import type { Session } from '../api/supabase.ts';
import type { Locale } from '../i18n/index.ts';
import { buildPath, homePath } from '../i18n/routes.ts';

export interface AuthGuardInput {
  /** The initial session check has not finished yet. */
  isLoading: boolean;
  session: Session | null;
  isActivated: boolean;
  pathname: string;
  locale: Locale;
}

/** The path to navigate to, or null if the customer is already where they belong. */
export function authGuardDecision(input: AuthGuardInput): string | null {
  if (input.isLoading) return null;

  const loginPath = buildPath('login', input.locale);
  const rotatePath = buildPath('rotate', input.locale);

  if (!input.session) {
    // Not logged in — can only see the login page.
    return input.pathname !== loginPath ? loginPath : null;
  }

  if (!input.isActivated) {
    // Logged in but not activated — can only see the rotation gate.
    return input.pathname !== rotatePath ? rotatePath : null;
  }

  // Fully authenticated — cannot see login or the rotation gate.
  if (input.pathname === loginPath || input.pathname === rotatePath) {
    return homePath(input.locale);
  }

  return null;
}
